import { differenceInDays, format, parseISO } from 'date-fns'
import deepEqual from 'fast-deep-equal'
import * as qs from 'qs'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { isValidDateString } from 'src/util/datetime/isValidDateString'
import { removeMatchingKeysAndValues } from 'src/util/removeMatchingKeysAndValues'

/**
 * 選択できる最大日数（開始日〜終了日）
 */
export const maxSelectableDays = 31

/**
 * 既定の対象期間（今日の単日）。
 * 日付未指定時に、API 取得・ピッカー表示の両方で使う。
 */
export const getDefaultDateRange = (): [string, string] => {
  const today = format(new Date(), 'yyyy-MM-dd')
  return [today, today]
}

/**
 * 1ページあたりの表示件数の選択肢
 */
export const rowsPerPageOptions = [25, 50, 100]

const defaultLimit = 25

/**
 * モニタリングダッシュボードの絞り込み・ページネーション条件
 *
 * scheduledReportSummary の useFilters を踏襲しつつ、
 * サーバーサイドページネーション用に offset / limit を加えている。
 */
export type MonitoringFilters = {
  /**
   * 開始日と終了日を持つ配列（ISO8601 拡張形式の日付 YYYY-MM-DD）
   */
  dateRange: [string, string] | null
  placeNodeUuids: string[]
  /** ページネーション開始位置 */
  offset: number
  /** 1ページあたりの件数 */
  limit: number
}

export const initialFilters: MonitoringFilters = {
  dateRange: null,
  placeNodeUuids: [],
  offset: 0,
  limit: defaultLimit,
}

const qsOptions = {
  // arrayLimit のデフォルト(20)だと 21要素以上の配列が qs.parse でオブジェクト化され、
  // sanitize で空配列に化けて絞り込みが解除されるため上限を引き上げる
  parse: { ignoreQueryPrefix: true, arrayLimit: 1000 },
  stringify: {
    skipNulls: true,
    encode: false,
  },
}

/**
 * 型の保証と不正な値のデフォルト値へのフォールバックを行う
 */
const sanitize = {
  dateRange: (arg: unknown): MonitoringFilters['dateRange'] => {
    const [arg1, arg2] = Array.isArray(arg) ? arg : []
    if (!isValidDateString(arg1) || !isValidDateString(arg2)) {
      return initialFilters.dateRange
    }
    const dateFrom = parseISO(arg1)
    const dateTo = parseISO(arg2)
    if (
      // 日付が未来であってはならない
      dateFrom > new Date() ||
      dateTo > new Date() ||
      // 開始日が終了日より後の日付であってはならない
      dateFrom > dateTo ||
      // 日付の差が最大選択可能日数を超えてはならない
      differenceInDays(dateTo, dateFrom) >= maxSelectableDays
    ) {
      return initialFilters.dateRange
    }
    return [arg1, arg2]
  },
  placeNodeUuids: (arg: unknown): string[] => {
    if (Array.isArray(arg)) {
      return arg.filter((uuid): uuid is string => typeof uuid === 'string')
    }
    return initialFilters.placeNodeUuids
  },
  offset: (arg: unknown): number => {
    const n = Number(arg)
    return Number.isInteger(n) && n >= 0 ? n : initialFilters.offset
  },
  limit: (arg: unknown): number => {
    const n = Number(arg)
    return rowsPerPageOptions.includes(n) ? n : initialFilters.limit
  },
}

export const useMonitoringFilters = () => {
  const history = useHistory()
  const location = useLocation()

  const getFiltersFromQueryString = (): MonitoringFilters => {
    const rawFilters = qs.parse(location.search, qsOptions.parse)
    return {
      dateRange: sanitize.dateRange(rawFilters['dateRange']),
      placeNodeUuids: sanitize.placeNodeUuids(rawFilters['placeNodeUuids']),
      offset: sanitize.offset(rawFilters['offset']),
      limit: sanitize.limit(rawFilters['limit']),
    }
  }

  const [filters, setFilters] = useState<MonitoringFilters>(() =>
    getFiltersFromQueryString(),
  )

  // 絞り込みが変更されたらURLを更新する
  useEffect(() => {
    // URLが散らかるのを防ぎたいため、初期値と同じ絞り込みはURLから取り除く
    const filtersToSet = removeMatchingKeysAndValues(filters, initialFilters)
    const newQueryString = qs.stringify(filtersToSet, qsOptions.stringify)

    const isAlreadySynchronized =
      newQueryString === history.location.search.substring(1)
    if (isAlreadySynchronized) return

    history.push({ search: newQueryString })
  }, [filters, history])

  // URLが変更されたら絞り込みを更新する
  useEffect(
    () => {
      const newFilters = getFiltersFromQueryString()

      const isAlreadySynchronized = deepEqual(newFilters, filters)
      if (isAlreadySynchronized) return

      // eslint-disable-next-line react-hooks/set-state-in-effect -- scheduledReportSummary/useFilters と同様の既存設計を踏襲
      setFilters(newFilters)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search],
  )

  const setFilterOf = {
    // 日付・現場を変更したらページネーションは先頭に戻す
    dateRange: (dateFrom: string, dateTo: string) => {
      setFilters(prev => ({
        ...prev,
        dateRange: sanitize.dateRange([dateFrom, dateTo]),
        offset: 0,
      }))
    },
    placeNodeUuids: (placeNodeUuids: string[]) => {
      setFilters(prev => ({
        ...prev,
        placeNodeUuids: sanitize.placeNodeUuids(placeNodeUuids),
        offset: 0,
      }))
    },
    offset: (offset: number) => {
      setFilters(prev => ({ ...prev, offset: sanitize.offset(offset) }))
    },
    // 件数変更時も先頭に戻す
    limit: (limit: number) => {
      setFilters(prev => ({ ...prev, limit: sanitize.limit(limit), offset: 0 }))
    },
  }

  const resetFilterOf = {
    dateRange: () => {
      setFilters(prev => ({ ...prev, dateRange: null, offset: 0 }))
    },
  }

  const isPlaceFilterActive = filters.placeNodeUuids.length > 0

  return {
    filters,
    isPlaceFilterActive,
    resetFilterOf,
    setFilterOf,
  }
}
